import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Wallet as WalletIcon, QrCode, ArrowClockwise, CheckCircle, XCircle, Clock, PaperPlaneTilt } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `\u20b9${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const PRESETS = [100, 500, 1000, 2000];

const StatusBadge = ({ s }) => {
  const map = {
    success: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-800",
    expired: "bg-slate-100 text-slate-500",
    failed: "bg-red-100 text-red-700",
  };
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${map[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;
};

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [amount, setAmount] = useState("");
  const [creating, setCreating] = useState(false);
  const [qr, setQr] = useState(null); // razorpay: {txn_id,image_url}; manual: {manual:true, amount}
  const [qrStatus, setQrStatus] = useState("pending");
  const [utr, setUtr] = useState("");
  const [utrMsg, setUtrMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [plans, setPlans] = useState(null);
  const [premModal, setPremModal] = useState(null); // {plan, price}
  const [premUtr, setPremUtr] = useState("");
  const [premMsg, setPremMsg] = useState("");
  const [premBusy, setPremBusy] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [w, t] = await Promise.all([axios.get(`${API}/wallet`), axios.get(`${API}/wallet/transactions`)]);
      setWallet(w.data); setTxns(t.data);
      try { const p = await axios.get(`${API}/premium/plans`); setPlans(p.data); } catch {}
    } catch (e) {
      if (e.response?.status === 403) setWallet({ forbidden: true });
    }
  };

  const submitPremiumUtr = async () => {
    setPremBusy(true); setPremMsg("");
    try {
      const r = await axios.post(`${API}/premium/subscribe`, { plan: premModal.plan, utr: premUtr });
      setPremMsg(r.data.message || "Request submitted for approval");
      setTimeout(() => { setPremModal(null); load(); }, 2500);
    } catch (e) {
      setPremMsg("ERR:" + (e.response?.data?.detail || "Request failed"));
    } finally { setPremBusy(false); }
  };
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  const startRecharge = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 100) { alert("Minimum recharge amount is ₹100"); return; }
    if (wallet?.mode === "manual") {
      setQr({ manual: true, amount: amt }); setUtr(""); setUtrMsg("");
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/wallet/recharge/qr`, { amount: amt });
      setQr(r.data); setQrStatus("pending");
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/wallet/recharge/${r.data.txn_id}/status`);
          setQrStatus(s.data.status);
          if (s.data.status !== "pending") { clearInterval(pollRef.current); load(); }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to generate QR");
    } finally { setCreating(false); }
  };

  const submitUtr = async () => {
    setSubmitting(true); setUtrMsg("");
    try {
      const r = await axios.post(`${API}/wallet/recharge/manual`, { amount: qr.amount, utr });
      setUtrMsg(r.data.message || "Request submitted for approval");
      setTimeout(() => { setQr(null); load(); }, 2200);
    } catch (e) {
      setUtrMsg("ERR:" + (e.response?.data?.detail || "Request failed"));
    } finally { setSubmitting(false); }
  };

  const closeQr = () => { clearInterval(pollRef.current); setQr(null); load(); };

  if (wallet?.forbidden) {
    return <div className="p-8 text-center text-slate-500" data-testid="wallet-forbidden">Wallet is available only for main admins.</div>;
  }

  return (
    <div className="p-4 sm:p-8" data-testid="wallet-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">Wallet</h1>
        <p className="text-slate-500 text-sm">Scan the UPI QR to add balance to your wallet</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Balance card */}
        <div className="bg-slate-900 text-white rounded-sm p-6" data-testid="wallet-balance-card">
          <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-widest font-bold mb-3">
            <WalletIcon size={18} weight="duotone" className="text-emerald-400" /> Current Balance
          </div>
          <p className="text-4xl font-black tracking-tight" data-testid="wallet-balance">{wallet ? fmt(wallet.balance) : "…"}</p>
          <button onClick={load} data-testid="wallet-refresh-btn" className="mt-4 text-xs text-slate-300 hover:text-white flex items-center gap-1">
            <ArrowClockwise size={14} /> Refresh
          </button>
        </div>

        {/* Recharge card */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6" data-testid="wallet-recharge-card">
          <h2 className="text-lg font-black text-slate-900 mb-1">Add Money (UPI)</h2>
          {wallet && wallet.mode === "none" && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-amber-800 text-xs" data-testid="wallet-upi-not-configured">
              UPI recharge is not active yet — the Super Admin needs to set a UPI ID (Super Admin → Wallet Management).
            </div>
          )}
          {wallet && wallet.mode === "manual" && (
            <p className="mb-4 text-xs text-slate-500" data-testid="wallet-manual-hint">
              Scan the QR, make the payment, then submit the UTR / reference number below. Your balance will be credited once the Super Admin approves the request.
            </p>
          )}
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESETS.map((p) => (
              <button key={p} onClick={() => setAmount(String(p))} data-testid={`wallet-preset-${p}`}
                className={`px-4 py-2 rounded-sm text-sm font-bold border transition-colors ${String(p) === amount ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
                {fmt(p)}
              </button>
            ))}
          </div>
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Amount (₹)</label>
              <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="wallet-amount-input"
                placeholder="Custom amount" className="px-3 py-2 border border-slate-300 rounded-sm text-sm w-44 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <button onClick={startRecharge} disabled={creating || !wallet || wallet.mode === "none"} data-testid="wallet-generate-qr-btn"
              className="px-5 py-2.5 bg-emerald-600 text-white rounded-sm text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
              <QrCode size={18} weight="bold" /> {creating ? "Generating…" : "Generate UPI QR"}
            </button>
          </div>
        </div>
      </div>

      {/* Premium Plan */}
      {plans && (
        <div className="mb-8 bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-sm p-6 border border-amber-500/40" data-testid="premium-plan-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="text-xl font-black flex items-center gap-2">👑 Premium Plan</h2>
              <p className="text-xs text-slate-300 mt-1">While the plan is active, EVERY document is FREE for the full year/month — no per-bill charges</p>
            </div>
            {plans.active && (
              <span className="px-3 py-1.5 bg-emerald-500/20 border border-emerald-400 text-emerald-300 rounded-sm text-xs font-black" data-testid="premium-active-badge">
                ✓ PREMIUM ACTIVE — {(plans.premium_start || "").slice(0, 10) || "today"} to {String(plans.premium_until).slice(0, 10)}
              </span>
            )}
            {plans.expired && (
              <span className="px-3 py-1.5 bg-red-500/20 border border-red-400 text-red-300 rounded-sm text-xs font-black" data-testid="premium-expired-badge">
                ⚠ PLAN EXPIRED on {String(plans.premium_until).slice(0, 10)}
              </span>
            )}
            {!plans.active && plans.pending_plan && (
              <span className="px-3 py-1.5 bg-amber-500/20 border border-amber-400 text-amber-300 rounded-sm text-xs font-black" data-testid="premium-pending-badge">
                ⏳ {plans.pending_plan.toUpperCase()} plan approval pending
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[{ k: "yearly", l: "Yearly Plan", price: plans.yearly_price, sub: "365 din unlimited free use", tag: "BEST VALUE" },
              { k: "monthly", l: "Monthly Plan", price: plans.monthly_price, sub: "30 din unlimited free use", tag: "" }].map((p) => (
              <div key={p.k} className={`rounded-sm p-4 border ${p.k === "yearly" ? "border-amber-400 bg-amber-500/10" : "border-slate-600 bg-slate-700/40"}`} data-testid={`premium-${p.k}-card`}>
                <div className="flex items-center justify-between">
                  <p className="font-black text-sm uppercase tracking-wide">{p.l}</p>
                  {p.tag && <span className="text-[9px] font-black bg-amber-400 text-slate-900 px-2 py-0.5 rounded-sm">{p.tag}</span>}
                </div>
                <p className="text-3xl font-black mt-2">{fmt(p.price)}<span className="text-xs font-semibold text-slate-400">/{p.k === "yearly" ? "year" : "month"}</span></p>
                <p className="text-[11px] text-slate-300 mt-1">{p.sub}</p>
                <button onClick={() => { setPremModal({ plan: p.k, price: p.price }); setPremUtr(""); setPremMsg(""); }}
                  disabled={!!plans.pending_plan || wallet?.mode === "none"}
                  data-testid={`premium-buy-${p.k}-btn`}
                  className="mt-3 w-full px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-sm text-sm font-black disabled:opacity-40">
                  {plans.active ? "Extend Plan" : "Buy Now"}
                </button>
              </div>
            ))}
          </div>
          {plans.expired && (
            <div className="mt-4 px-4 py-3 bg-red-500/15 border border-red-400 rounded-sm text-red-200 text-xs font-bold" data-testid="premium-expired-banner">
              ⚠ Your {plans.premium_plan === "yearly" ? "Yearly" : "Monthly"} Premium Plan expired on {String(plans.premium_until).slice(0, 10)}.
              Normal per-document charges now apply. Renew the plan above to continue free usage.
            </div>
          )}
        </div>
      )}

      {/* Premium buy modal */}
      {premModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="premium-modal">
          <div className="bg-white rounded-sm p-6 max-w-sm w-full text-center">
            <h3 className="text-lg font-black text-slate-900 mb-1">👑 {premModal.plan === "yearly" ? "Yearly" : "Monthly"} Plan — {fmt(premModal.price)}</h3>
            <p className="text-xs text-slate-500 mb-4">Scan the UPI QR to pay, then submit the UTR number below</p>
            <img src={`${API}/wallet/recharge/manual-qr?amount=${premModal.price}`} alt="UPI QR" crossOrigin="use-credentials"
              className="mx-auto w-56 h-auto border border-slate-200 rounded-sm" data-testid="premium-qr-image" />
            {wallet?.upi_id && <p className="mt-2 text-xs font-mono font-bold text-slate-700">{wallet.upi_id}</p>}
            <div className="mt-4 text-left">
              <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Enter the UTR / reference number after payment</label>
              <input value={premUtr} onChange={(e) => setPremUtr(e.target.value)} data-testid="premium-utr-input"
                placeholder="e.g. 415712345678" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-2" />
              {premMsg && (
                <div className={`text-xs p-2 rounded-sm mb-2 ${premMsg.startsWith("ERR:") ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="premium-msg">
                  {premMsg.replace("ERR:", "")}
                </div>
              )}
              <button onClick={submitPremiumUtr} disabled={premBusy || premUtr.trim().length < 6} data-testid="premium-utr-submit-btn"
                className="w-full px-4 py-2.5 bg-slate-900 text-white rounded-sm text-sm font-bold hover:bg-slate-800 disabled:opacity-50">
                {premBusy ? "Submitting…" : "I have paid — Submit for approval"}
              </button>
            </div>
            <button onClick={() => setPremModal(null)} data-testid="premium-modal-close" className="mt-3 px-5 py-2 border border-slate-300 rounded-sm text-sm font-semibold text-slate-700 hover:bg-slate-100 w-full">Close</button>
          </div>
        </div>
      )}

      {/* QR modal */}
      {qr && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="wallet-qr-modal">
          <div className="bg-white rounded-sm p-6 max-w-sm w-full text-center">
            <h3 className="text-lg font-black text-slate-900 mb-1">Scan &amp; Pay {fmt(qr.amount)}</h3>
            <p className="text-xs text-slate-500 mb-4">Scan with GPay, PhonePe, Paytm or any other UPI app</p>

            {qr.manual ? (
              <>
                <img src={`${API}/wallet/recharge/manual-qr?amount=${qr.amount}`} alt="UPI QR" crossOrigin="use-credentials"
                  className="mx-auto w-56 h-auto border border-slate-200 rounded-sm" data-testid="wallet-manual-qr-image" />
                {wallet?.upi_id && <p className="mt-2 text-xs font-mono font-bold text-slate-700" data-testid="wallet-upi-id">{wallet.upi_id}</p>}
                <div className="mt-4 text-left">
                  <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Enter the UTR / reference number after payment</label>
                  <input value={utr} onChange={(e) => setUtr(e.target.value)} data-testid="wallet-utr-input"
                    placeholder="e.g. 415712345678" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-2" />
                  {utrMsg && (
                    <div className={`text-xs p-2 rounded-sm mb-2 ${utrMsg.startsWith("ERR:") ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="wallet-utr-msg">
                      {utrMsg.replace("ERR:", "")}
                    </div>
                  )}
                  <button onClick={submitUtr} disabled={submitting || utr.trim().length < 6} data-testid="wallet-utr-submit-btn"
                    className="w-full px-4 py-2.5 bg-slate-900 text-white rounded-sm text-sm font-bold hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2">
                    <PaperPlaneTilt size={16} weight="bold" /> {submitting ? "Submitting…" : "I have paid — Submit for approval"}
                  </button>
                </div>
              </>
            ) : (
              <>
                {qrStatus === "pending" && (
                  <>
                    <img src={qr.image_url} alt="UPI QR" className="mx-auto w-64 h-auto border border-slate-200 rounded-sm" data-testid="wallet-qr-image" />
                    <div className="mt-4 flex items-center justify-center gap-2 text-amber-600 text-sm font-semibold" data-testid="wallet-qr-waiting">
                      <Clock size={16} className="animate-pulse" /> Waiting for payment…
                    </div>
                  </>
                )}
                {qrStatus === "success" && (
                  <div className="py-8" data-testid="wallet-qr-success">
                    <CheckCircle size={64} weight="fill" className="text-emerald-500 mx-auto mb-3" />
                    <p className="text-emerald-700 font-black text-lg">{fmt(qr.amount)} has been added to your wallet.</p>
                  </div>
                )}
                {(qrStatus === "expired" || qrStatus === "failed") && (
                  <div className="py-8" data-testid="wallet-qr-failed">
                    <XCircle size={64} weight="fill" className="text-red-400 mx-auto mb-3" />
                    <p className="text-red-600 font-bold">QR expired — please try again</p>
                  </div>
                )}
              </>
            )}

            <button onClick={closeQr} data-testid="wallet-qr-close-btn" className="mt-4 px-5 py-2 border border-slate-300 rounded-sm text-sm font-semibold text-slate-700 hover:bg-slate-100 w-full">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Transactions */}
      <div className="bg-white border border-slate-200 rounded-sm sticky-table" data-testid="wallet-txns-table">
        <table className="w-full text-sm">
          <thead className="sticky-thead bg-slate-50 text-slate-600 text-xs uppercase font-bold">
            <tr>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Method</th>
              <th className="px-4 py-3 text-left">Note</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {txns.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No transactions yet</td></tr>
            )}
            {txns.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`wallet-txn-${t.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{(t.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3"><span className={`font-bold text-xs uppercase ${t.type === "credit" ? "text-emerald-700" : "text-red-700"}`}>{t.type}</span></td>
                <td className="px-4 py-3 text-xs uppercase text-slate-500">{t.method === "upi_manual" ? "UPI (manual)" : t.method}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{t.note || "—"}</td>
                <td className="px-4 py-3"><StatusBadge s={t.status} /></td>
                <td className={`px-4 py-3 text-right font-mono font-bold ${t.type === "credit" ? "text-emerald-700" : "text-red-700"}`}>
                  {t.type === "credit" ? "+" : "−"}{fmt(t.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
